import { useState } from 'react';
import { cn, getFlagUrl } from '@/lib/utils';

interface FlagImageProps {
  countryCode: string;
  countryName?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  loading?: 'lazy' | 'eager';
}

// Size classes - smaller sizes are width-based, xl is height-based for quiz display
const sizeClasses = {
  xs: 'w-8 h-auto',
  sm: 'w-12 h-auto',
  md: 'w-20 h-auto',
  lg: 'w-32 h-auto',
  xl: 'w-auto h-[170px]',
};

// Max dimensions to prevent extreme sizes
const maxSizeClasses = {
  xs: 'max-h-8',
  sm: 'max-h-12',
  md: 'max-h-20',
  lg: 'max-h-32',
  xl: 'max-w-[340px]',
};

export function FlagImage({
  countryCode,
  countryName,
  size = 'md',
  className,
  loading = 'lazy',
}: FlagImageProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => setIsLoading(false);
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError) {
    return (
      <div
        className={cn(
          sizeClasses[size],
          'aspect-[3/2]',
          'flex items-center justify-center',
          'bg-gray-200 dark:bg-gray-700 rounded-lg',
          'text-gray-400 dark:text-gray-500 text-xs',
          className
        )}
      >
        {countryCode.toUpperCase()}
      </div>
    );
  }

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      {isLoading && (
        <div
          className={cn(
            sizeClasses[size],
            'aspect-[3/2]',
            'bg-gray-200 dark:bg-gray-700 rounded-lg',
            'animate-pulse'
          )}
        />
      )}
      <img
        src={getFlagUrl(countryCode)}
        alt={countryName ? `Flag of ${countryName}` : `Flag ${countryCode}`}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          sizeClasses[size],
          maxSizeClasses[size],
          'rounded-lg shadow-md',
          isLoading && 'absolute opacity-0',
          'transition-opacity duration-200'
        )}
      />
    </div>
  );
}
